/**
 * usePersonByEmail — resolve an email address to a CRM entity, store-first.
 *
 * Subscribes to the CRM object store's `emailIndex` so resolution stays in
 * sync with WebSocket events (object_created / property_updated /
 * object_merged / object_deleted) without going through React Query
 * invalidation. The first time we see an unknown email, we fire a single
 * search call to populate the store; from then on, the store is the source
 * of truth and the hook just re-renders when the index changes.
 *
 * Resolution order (mirrors the previous behavior):
 *   1. ahoy_user
 *   2. ahoy_contact
 */

import { useCallback, useEffect, useSyncExternalStore } from 'react';

import { CONTACT, isPersonType, USER } from '@/lib/crm/core/entityTypes';
import { useCrmObjectSnapshot } from '@/lib/crm/hooks/useCrmObjectSnapshot';
import {
  EMAIL_INDEX_UNKNOWN,
  getEmailIndexSnapshot,
  getSnapshot as getStoreSnapshot,
  getStoreGeneration,
  markEmailIndexUnresolved,
  registerResolvedEmail,
  resolveEntityId,
  subscribeToEmailIndex,
  upsertObject,
} from '@/lib/crm/store/crmObjectStore';
import { fetchWithDefaults } from '@/lib/http/client';
import type { EntitySearchResponse } from '@/lib/search/types';
import type { CrmObject, PropertyValue } from '@/types/crm';

export interface ResolvedPerson {
  type: 'ahoy_user' | 'ahoy_contact';
  entityId: string;
  name: string;
}

const SEARCH_TYPES = [USER, CONTACT].join(',');

// Module-level dedupe map keyed by `${orgId}|${normalizedEmail}`. When the
// hook mounts for an unknown email it kicks off a search; concurrent mounts
// for the same email share the in-flight Promise so we don't fire N parallel
// requests for the same address (a common case in email-thread views with
// many chips for the same recipient).
const inFlightSearches = new Map<string, Promise<void>>();

interface SearchPersonResult {
  type: 'ahoy_user' | 'ahoy_contact';
  entity_id: string;
  name: string;
  email: string;
}

/**
 * Pick the best resolution from a search response.
 *
 * The search API uses `icontains` for text matching, so the first result is
 * NOT guaranteed to be an exact email match — searching `ada@x.com` can
 * return `ada-old@x.com` first if there's no exact match. And the `email`
 * field on each result is `_first(ahoy_email)`, so for multi-email entities
 * (`[a@x.com, b@x.com]` searched as `b@x.com`) the result's `email` would be
 * `a@x.com`, missing a valid match if we naively compared. We filter against
 * the full `emails` array (with fallback to `email` for older API
 * responses) to find the exact match.
 */
function _pickBestMatch(
  data: EntitySearchResponse,
  normalizedEmail: string
): SearchPersonResult | null {
  const hasExactMatch = (result: {
    email?: string;
    emails?: string[];
  }): boolean => {
    const list = result.emails ?? (result.email ? [result.email] : []);
    return list.some(e => e.trim().toLowerCase() === normalizedEmail);
  };

  const user = data.users?.find(hasExactMatch);
  if (user) {
    return {
      type: 'ahoy_user',
      entity_id: user.entity_id,
      name: user.name,
      email: user.email,
    };
  }
  const contact = data.contacts?.find(hasExactMatch);
  if (contact) {
    return {
      type: 'ahoy_contact',
      entity_id: contact.entity_id,
      name: contact.name,
      email: contact.email,
    };
  }
  return null;
}

/**
 * Build a minimal CrmObject from a search result. Goes into the store as
 * a partial entry so the email index can resolve to this entity_id; full
 * properties / associations get filled in later by ensureEntity or by the
 * next WebSocket update.
 *
 * IMPORTANT: we include `ahoy_email` in the seeded properties so the
 * store's `_reindexEntityEmails` (called inside upsertObject) picks it up
 * and registers the index entry. Without that, the subscriber that just
 * fired this search would not see a positive index entry on the next read.
 */
function _seedStoreFromSearch(
  result: SearchPersonResult,
  orgId: number,
  email: string
): void {
  const properties: Record<string, PropertyValue> = {
    ahoy_email: { value: [email] } as PropertyValue,
  };
  if (result.name) {
    properties.ahoy_display_name = { value: result.name } as PropertyValue;
  }
  const obj: CrmObject = {
    id: result.entity_id,
    org_id: orgId,
    entity_id: result.entity_id,
    entity_type_name: result.type,
    properties,
    associations: undefined,
    last_event_seq: 0,
    deleted_at: null,
  };
  upsertObject(obj);
  // The upsert alone is NOT guaranteed to register the email: when the
  // store already holds this entity at a newer seq with a lean
  // keyed-but-empty `ahoy_email` (timeline/batch seeds), the older-seq
  // merge keeps the empty value and reindexing skips the address —
  // leaving the subscriber that fired this search loading forever.
  // Register the server-confirmed mapping explicitly (issue #10430).
  registerResolvedEmail(result.entity_id, result.type, email);
}

// Emails whose stale (non-person) index mapping we have already tried to
// recover with a one-shot search, keyed by `${orgId}|${normalizedEmail}`. A
// positive index mapping blocks `markEmailIndexUnresolved`, so a recovery
// search that finds nothing leaves the stale mapping in place — without this
// bound, `_shouldSearchEmail` would keep returning `true` and re-fire the
// search on every effect run.
const staleMappingRecoveries = new Set<string>();

// The store generation the entries above were recorded under. `clearAll()`
// wipes the email index and bumps the generation, and it runs on more than the
// org switch (changing a list's entity type clears the store too). An
// exhausted recovery attempt only makes sense against the index state that
// produced it, so entries are scoped to their generation: once the store is
// wiped, every recorded attempt is dropped and a stale mapping seen again gets
// a fresh recovery search instead of being permanently skipped.
let staleMappingRecoveryGeneration = getStoreGeneration();

/**
 * Drop recorded recovery attempts when the store has been wiped since they
 * were recorded. Called on every `_shouldSearchEmail` read so the tracker can
 * never outlive the index state it was reasoning about.
 */
function _syncStaleMappingRecoveryGeneration(): void {
  const generation = getStoreGeneration();
  if (generation !== staleMappingRecoveryGeneration) {
    staleMappingRecoveryGeneration = generation;
    staleMappingRecoveries.clear();
  }
}

/**
 * Decide whether an email still needs a search, and claim the attempt.
 *
 * `true` when either:
 *   - the index has never been asked about this email (`EMAIL_INDEX_UNKNOWN`), or
 *   - the index holds a positive mapping that does NOT point at a person
 *     entity in the store (a non-person entity, or an entity we have no store
 *     entry for at all). Such a mapping is stale cache state left behind by a
 *     torn-down entity type; it is NOT a definitive answer, because a real
 *     contact / user for this address may exist in the backend. We allow
 *     exactly one recovery search per `(orgId, email)` to find it — a search
 *     that lands on a contact / user re-points the index (the store resolves
 *     same-email collisions by entity-type rank), and one that finds nothing
 *     leaves the participant unresolved (plain badge → Create Contact).
 *
 * `false` for a confirmed-unresolved (`null`) entry and for a mapping that
 * already points at a person — both are definitive.
 *
 * Side effect: records the recovery attempt when it returns `true` for a
 * stale mapping, so the caller must only invoke this when it is about to
 * search.
 */
function _shouldSearchEmail(normalizedEmail: string, orgId: number): boolean {
  _syncStaleMappingRecoveryGeneration();
  const snapshot = getEmailIndexSnapshot(normalizedEmail);
  if (snapshot === EMAIL_INDEX_UNKNOWN) {
    return true;
  }
  if (typeof snapshot !== 'string') {
    return false;
  }
  const stored = getStoreSnapshot().get(resolveEntityId(snapshot))?.data;
  if (stored && isPersonType(stored.entity_type_name)) {
    return false;
  }
  const recoveryKey = `${orgId}|${normalizedEmail}`;
  if (staleMappingRecoveries.has(recoveryKey)) {
    return false;
  }
  staleMappingRecoveries.add(recoveryKey);
  return true;
}

/**
 * Clear the one-shot recovery-search tracker for stale non-person email
 * mappings. Any store wipe already invalidates the tracker via the generation
 * check above, so this is a belt-and-braces reset for the org-switch cleanup
 * lifecycle (alongside `clearCrmObjectStore`) and the hook that tests use to
 * reset module state between cases.
 */
export function resetStaleMappingRecoveries(): void {
  staleMappingRecoveries.clear();
  staleMappingRecoveryGeneration = getStoreGeneration();
}

/**
 * Trigger the email→entity resolution for a single email and seed the
 * store. Idempotent — concurrent calls for the same `(orgId, email)` pair
 * share a single network request via the module-level `inFlightSearches`
 * map. Safe to call from `useEffect` over a list of emails (e.g. the
 * participants section ensures every raw email gets resolved so chips
 * upgrade from "email" to a resolved user/contact when the entity exists).
 *
 * Returns the in-flight Promise so callers can await completion (mostly
 * for tests); production callers fire-and-forget.
 */
export function ensureEmailResolution(
  email: string,
  orgId: number
): Promise<void> {
  const normalizedEmail = email.trim().toLowerCase();
  if (!normalizedEmail || !Number.isFinite(orgId) || orgId <= 0) {
    return Promise.resolve();
  }
  // Skip if the index already has a definitive answer (a mapping to a person
  // entity OR confirmed-unresolved null). Without this, callers that re-run on
  // participant-list updates (e.g. useThreadParticipants paginating
  // timeline events) would fire a new network request for every email on
  // every effect run, since the in-flight map is cleared once the search
  // completes. The negative cache is invalidated automatically when an
  // apply event surfaces an entity with this email, so a later call will
  // re-fire when there's actually new information.
  if (!_shouldSearchEmail(normalizedEmail, orgId)) {
    return Promise.resolve();
  }
  const key = `${orgId}|${normalizedEmail}`;
  const existing = inFlightSearches.get(key);
  if (existing) {
    return existing;
  }
  const promise = _searchAndPopulate(normalizedEmail, orgId, normalizedEmail);
  inFlightSearches.set(key, promise);
  promise.finally(() => {
    if (inFlightSearches.get(key) === promise) {
      inFlightSearches.delete(key);
    }
  });
  return promise;
}

/**
 * Module-level "active org" tracker. Each `usePersonByEmail` mount writes
 * the org it's currently resolving for; in-flight searches compare this
 * against the org they were fired for and drop their result when the user
 * has switched orgs in the meantime. Without this guard, a late response
 * from a previous org would seed `crmObjectStore` with entities that don't
 * belong to the now-active org and silently corrupt the email index.
 *
 * `ensureEmailResolution` (which fires from non-hook contexts) also passes
 * its caller-supplied orgId through to `_searchAndPopulate`; the same
 * check applies, so a stale background resolution is dropped on org switch.
 */
let activeOrgIdForEmailResolution: number | null = null;

/**
 * Set the module-level active-org guard used by `_searchAndPopulate` to
 * drop stale results after an org switch. Called by every hook that
 * triggers an email→entity resolution so the guard reflects the org the
 * UI is currently viewing — without this, a hook that fires
 * `ensureEmailResolution` from a fresh org would have its results dropped
 * because the guard still held the previous org's id.
 */
export function setActiveOrgIdForEmailResolution(orgId: number | null): void {
  activeOrgIdForEmailResolution = orgId;
}

export function _setActiveOrgIdForTesting(orgId: number | null): void {
  setActiveOrgIdForEmailResolution(orgId);
}

async function _searchAndPopulate(
  email: string,
  orgId: number,
  normalizedEmail: string
): Promise<void> {
  const params = new URLSearchParams({
    q: normalizedEmail,
    types: SEARCH_TYPES,
    include_hidden: 'true',
  });
  try {
    const response = await fetchWithDefaults(
      `/api/orgs/${orgId}/search/?${params.toString()}`
    );
    // Org-staleness guard: if the active org has changed since this search
    // was kicked off, drop the result rather than writing it to the global
    // store. Late responses from a previous org must NOT populate the new
    // org's email index — that would silently mis-resolve people across
    // tenants.
    if (
      activeOrgIdForEmailResolution !== null &&
      activeOrgIdForEmailResolution !== orgId
    ) {
      return;
    }
    if (!response.ok) {
      // Mark unresolved on HTTP error so the chip falls through to the
      // email-as-display affordance (click → create modal) instead of
      // staying in a perpetual loading state. Any apply event surfacing
      // an entity with this email later clears the negative entry.
      markEmailIndexUnresolved(normalizedEmail);
      return;
    }
    const data: EntitySearchResponse = await response.json();
    // Re-check after the JSON parse — a slow body read could span an org
    // switch between header receipt and result availability.
    if (
      activeOrgIdForEmailResolution !== null &&
      activeOrgIdForEmailResolution !== orgId
    ) {
      return;
    }
    const best = _pickBestMatch(data, normalizedEmail);
    if (best) {
      _seedStoreFromSearch(best, orgId, normalizedEmail);
    } else {
      // Confirmed unresolved — record so subsequent renders skip re-search
      // until an apply event (object_created / property_updated) surfaces
      // an entity with this email and clears the negative entry.
      markEmailIndexUnresolved(normalizedEmail);
    }
  } catch {
    // Same rationale as !response.ok: avoid leaving the chip in loading
    // state on transient network errors. Skip when the org has switched —
    // the new org will trigger its own resolution.
    if (
      activeOrgIdForEmailResolution !== null &&
      activeOrgIdForEmailResolution !== orgId
    ) {
      return;
    }
    markEmailIndexUnresolved(normalizedEmail);
  }
}

/**
 * Resolve an email to a user / contact entity via the store. Backed by
 * `crmObjectStore.emailIndex`; falls back to a single search call on first
 * sight of a previously-unknown email.
 */
export function usePersonByEmail(
  email: string,
  orgId: string | undefined
): { person: ResolvedPerson | null; isLoading: boolean } {
  const normalizedEmail = email.trim().toLowerCase();
  const orgIdNumber =
    orgId !== undefined && orgId !== '' ? Number(orgId) : undefined;
  const enabled =
    !!orgIdNumber &&
    Number.isFinite(orgIdNumber) &&
    normalizedEmail.length >= 2;

  // Subscribe to the store's email index so the snapshot updates on
  // upsertObject / property updates / merges / deletes for this address.
  const subscribe = useCallback(
    (listener: () => void) => {
      if (!enabled) {
        return () => {};
      }
      return subscribeToEmailIndex(normalizedEmail, listener);
    },
    [enabled, normalizedEmail]
  );
  const getSnapshot = useCallback(() => {
    if (!enabled) {
      return null;
    }
    return getEmailIndexSnapshot(normalizedEmail);
  }, [enabled, normalizedEmail]);

  const indexSnapshot = useSyncExternalStore(subscribe, getSnapshot);

  // Track which org this hook instance is resolving for. Late responses
  // from a previous org are dropped by `_searchAndPopulate` against the
  // module-level `activeOrgIdForEmailResolution` to prevent stale results
  // from corrupting the new org's email index.
  useEffect(() => {
    if (enabled && orgIdNumber !== undefined) {
      setActiveOrgIdForEmailResolution(orgIdNumber);
    }
  }, [enabled, orgIdNumber]);

  // Resolve through aliases so a post-merge email returns the canonical id
  // even if the index briefly held the source id (the store's apply path
  // transfers entries via `_transferEntityEmails`, but defense-in-depth is
  // cheap here).
  const resolvedEntityId =
    typeof indexSnapshot === 'string' ? resolveEntityId(indexSnapshot) : null;

  // Subscribe to the resolved entity in the store so the hook returns a
  // current display name (the search seed may be stale by the time the
  // user clicks; WS events refresh the entity in the store).
  const storeObject = useCrmObjectSnapshot(
    resolvedEntityId ?? undefined,
    orgIdNumber
  );
  const storeEntityType = storeObject?.entity_type_name;

  // Fire a one-shot search when the index has no definitive answer — either
  // it has never been asked about this email, or it holds a stale mapping to
  // a non-person entity (see `_shouldSearchEmail`). The module-level
  // `inFlightSearches` map dedupes concurrent mounts requesting the same
  // (orgId, email) pair.
  //
  // We deliberately do NOT keep a `lastFetchedRef` cache here — gating on a
  // remembered key prevents re-fetching after the store is reset (e.g. on
  // org switch via `clearAll`, which puts the index back to UNKNOWN). The
  // store's own caches (`emailIndex`, `emailIndexNegative`) already make
  // resolved/known-unresolved snapshots skip the effect; the in-flight map
  // dedupes the rest. Without this change, post-reset renders saw
  // `indexSnapshot === EMAIL_INDEX_UNKNOWN` but skipped the search because
  // the ref still held the old key, leaving the hook stuck on `isLoading`.
  useEffect(() => {
    if (!enabled || orgIdNumber === undefined) {
      return;
    }
    if (!_shouldSearchEmail(normalizedEmail, orgIdNumber)) {
      return;
    }
    const key = `${orgIdNumber}|${normalizedEmail}`;
    let inFlight = inFlightSearches.get(key);
    if (!inFlight) {
      inFlight = _searchAndPopulate(
        normalizedEmail,
        orgIdNumber,
        normalizedEmail
      );
      inFlightSearches.set(key, inFlight);
      inFlight.finally(() => {
        // Clean up so a later org-switch / cache reset can re-fetch.
        if (inFlightSearches.get(key) === inFlight) {
          inFlightSearches.delete(key);
        }
      });
    }
    // `indexSnapshot` / `storeEntityType` are dependencies (not read directly
    // — `_shouldSearchEmail` re-reads the store) so the gate is re-evaluated
    // when the index changes or the mapped entity hydrates into a type we can
    // classify.
  }, [enabled, indexSnapshot, normalizedEmail, orgIdNumber, storeEntityType]);

  if (!enabled) {
    return { person: null, isLoading: false };
  }
  if (indexSnapshot === EMAIL_INDEX_UNKNOWN) {
    return { person: null, isLoading: true };
  }
  if (indexSnapshot === null || !resolvedEntityId) {
    return { person: null, isLoading: false };
  }
  // Only a mapping we can positively confirm points at a person entity
  // (contact / user) resolves. A mapping to a non-person entity — or to an
  // entity we have no store entry for, so its type is unknown — is stale
  // index state left behind by a torn-down entity type; coercing it into an
  // `ahoy_contact` badge would give the chip the wrong identity, the wrong
  // navigation target, and hide the Create Contact affordance. It falls
  // through to unresolved instead, while the effect above fires a one-shot
  // recovery search so a real contact / user in the backend still surfaces.
  // Mirrors the guard in `useStoreResolvedParticipants`.
  if (!isPersonType(storeEntityType)) {
    return { person: null, isLoading: false };
  }

  // Best-effort name extraction. An empty string passes the `typeof` guard,
  // so explicitly require a non-empty value before using the stored display
  // name — otherwise chips would render with no visible text when the store
  // hasn't seeded one yet.
  const displayNameValue =
    storeObject?.properties?.ahoy_display_name?.value ?? '';
  const name =
    typeof displayNameValue === 'string' && displayNameValue
      ? displayNameValue
      : normalizedEmail;
  const entityType = storeEntityType === USER ? USER : CONTACT;

  return {
    person: { type: entityType, entityId: resolvedEntityId, name },
    isLoading: false,
  };
}
